import type {
  CustomFormAnswerEntry,
  CustomFormAnswerMap,
  CustomFormField,
  CustomFormFieldOptions,
  CustomFormResponseFieldSnapshot,
} from "./types";
import type { CustomFormFieldPayload } from "./models/payloads";

export type CustomFormFieldLike = CustomFormField | CustomFormResponseFieldSnapshot | CustomFormFieldPayload;
export type CustomFormAnswerValue = CustomFormAnswerEntry["value"];

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function fieldChoices(field: CustomFormFieldLike): NonNullable<CustomFormFieldOptions["choices"]> {
  return field.options?.choices ?? [];
}

// Renders an answer for read-only display: resolves select values to their labels,
// joins multi-select/array values, and falls back to "—" for empty values.
// Shared by ResponseDetailDrawer's field-by-field view and the responses grid's cells.
export function formatReadonlyValue(field: CustomFormFieldLike, value: CustomFormAnswerValue | undefined): string {
  if (value === null || value === undefined || value === "") return "—";

  switch (field.fieldType) {
    case "single_select": {
      const choice = fieldChoices(field).find((c) => c.value === value);
      return choice?.label ?? String(value);
    }
    case "multi_select": {
      const choices = fieldChoices(field);
      const values = Array.isArray(value) ? value : [];
      if (values.length === 0) return "—";
      return values.map((v) => choices.find((c) => c.value === v)?.label ?? v).join(", ");
    }
    case "short_text":
    case "long_text":
    case "number":
    case "date":
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

export function buildAnswerEntries(fields: CustomFormField[], values: CustomFormAnswerMap): CustomFormAnswerEntry[] {
  return fields.map((field) => ({ fieldUUID: field.uuid, value: values[field.uuid] ?? null }));
}

export function validateRequired(fields: CustomFormField[], values: CustomFormAnswerMap): Record<string, string> {
  const errors: Record<string, string> = {};
  fields.forEach((field) => {
    if (!field.required) return;
    const value = values[field.uuid];
    const isEmpty =
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    if (isEmpty) errors[field.uuid] = `${field.label} is required`;
  });
  return errors;
}

